import type { SkillKey, ApiMessage, ConversationsResponse } from './types'

const API_BASE_URL = 'https://aitool.ttcdw.cn/v1'
const GROWTH_API_BASE_URL = 'https://aitool.ttcdw.cn/v1'

/**
 * 获取技能对应的 API 基础地址
 */
const getApiBaseUrl = (skill: SkillKey): string => {
  return skill === 'growth' ? GROWTH_API_BASE_URL : API_BASE_URL
}

/**
 * 从环境变量获取 Token
 */
const getAuthToken = (skill: SkillKey): string => {
  const tokens: Record<SkillKey, string | undefined> = {
    activity: import.meta.env.VITE_APP_TOKEN_ACTIVITY,
    system: import.meta.env.VITE_APP_TOKEN_SYSTEM,
    learning: import.meta.env.VITE_APP_TOKEN_LEARNING,
    growth: import.meta.env.VITE_APP_TOKEN_GROWTH,
  }
  return tokens[skill] || ''
}

const commonHeaders = {
  'Content-Type': 'application/json',
}

/**
 * 检查响应状态并抛出错误
 */
const checkStatus = async (response: Response): Promise<Response> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const error = new Error(JSON.stringify({
      code: errorData.code || 'unknown',
      message: errorData.message || response.statusText,
      status: response.status
    }))
    error.name = 'ApiError'
    throw error
  }
  return response
}

/**
 * 发送消息（流式响应）
 */
export const sendMessage = async (
  skill: SkillKey,
  query: string,
  conversationId: string | null,
  user: string,
  signal?: AbortSignal
): Promise<Response> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/chat-messages`, {
    method: 'POST',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      inputs: {},
      query,
      response_mode: 'streaming',
      conversation_id: conversationId || '',
      user,
      files: [],
    }),
    signal, // 添加 abort signal
  })

  return checkStatus(response)
}

/**
 * 获取会话列表
 */
export const getConversations = async (
  skill: SkillKey,
  user: string,
  limit: number = 20
): Promise<ConversationsResponse> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/conversations?user=${user}&limit=${limit}`, {
    method: 'GET',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
  })

  await checkStatus(response)
  return response.json()
}

/**
 * 获取消息历史
 */
export const getMessageHistory = async (
  skill: SkillKey,
  user: string,
  conversationId: string
): Promise<ApiMessage[]> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/messages?user=${user}&conversation_id=${conversationId}`, {
    method: 'GET',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
  })
  
  await checkStatus(response)
  return response.json()
}

/**
 * 删除会话
 */
export const deleteConversation = async (
  skill: SkillKey,
  conversationId: string,
  user: string
): Promise<void> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ user }),
  })
  
  await checkStatus(response)
}

/**
 * 发送反馈
 */
export const sendFeedback = async (
  skill: SkillKey,
  messageId: string,
  rating: 'like' | 'dislike',
  user: string
): Promise<void> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/messages/${messageId}/feedbacks`, {
    method: 'POST',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      rating,
      user,
    }),
  })
  
  await checkStatus(response)
}

/**
 * 取消反馈
 */
export const cancelFeedback = async (
  skill: SkillKey,
  messageId: string,
  user: string
): Promise<void> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/messages/${messageId}/feedbacks`, {
    method: 'POST',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      user,
    }),
  })
  
  await checkStatus(response)
}

/**
 * 获取推荐问题
 */
export const getSuggestedQuestions = async (
  skill: SkillKey,
  messageId: string,
  user: string
): Promise<string[]> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/messages/${messageId}/suggested?user=${user}`, {
    method: 'GET',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
  })
  
  await checkStatus(response)
  return response.json()
}

/**
 * 停止响应
 */
export const stopResponse = async (
  skill: SkillKey,
  taskId: string,
  user: string
): Promise<void> => {
  const authToken = getAuthToken(skill)
  const baseUrl = getApiBaseUrl(skill)
  const response = await fetch(`${baseUrl}/chat-messages/${taskId}/stop`, {
    method: 'POST',
    headers: {
      ...commonHeaders,
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ user }),
  })
  
  await checkStatus(response)
}
